import React, { useEffect, useState } from 'react';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import percentageIcon from '@/assets/icons/percentage.svg';
import euroIcon from '@/assets/icons/euro.svg';
import PieGraph from '@/components/organisms/graphs/PieGraph';
import { useNavigate, useParams } from 'react-router-dom';
import AllocationBar from '@/components/organisms/AllocationBar';
import { expenseRevenueService } from '@/services/expense-category.service';
import InputField from '@/components/atoms/InputField';
import { Button } from '@/components/ui/button';
import toast from 'react-hot-toast';
import MobileAppBar from '@/components/molecules/MobileAppBar';
import { budgetService } from '@/services/budget.service';
const ECAdjustPage = () => {
  const [graphViewMode, setGraphViewMode] = useState('percentage');
  const [expenseCategories, setExpenseCategories] = useState<any[]>([]);
  const [editedValues, setEditedValues] = useState<{ [id: string]: number }>({});
  const [isInitialized, setIsInitialized] = useState(false);
  const [totalAmount, setTotalAmount] = useState(0);
  const [budgetData, setBudgetData] = useState<any>(null);
  const [totalBudget, setTotalBudget] = useState({ ecmev: 0, ecmev_used: 0 });

  const { budgetId } = useParams();
  const navigate = useNavigate();

  // Calculate maximum allowed total
  const maxAllowedTotal = totalBudget.ecmev;

  const ecColors = [
    '#7663EB', // First
    '#48C80C', // Second
    '#007AFF', // Third
    '#00C7BE', // Fourth
    '#A2845E', // Fifth
    '#FF9500', // Sixth
    '#FF2D55', // Seventh
    '#FFCC00', // Eighth
    '#94B665', // Ninth
    '#774E19' // Tenth
  ];

  const fetchExpenseCategories = async () => {
    try {
      const { data: expenseCategoriesData } = await expenseRevenueService.getExpenseCategories(budgetId || '');

      // Calculate totals for all expense categories
      const totals = expenseCategoriesData.reduce(
        (acc, ec) => ({
          ecmev: acc.ecmev + (ec.ecmev || 0),
          ecmev_used: acc.ecmev_used + (ec.ecmev_used || 0)
        }),
        { ecmev: 0, ecmev_used: 0 }
      );

      setTotalBudget(totals);

      const formattedExpenseCategories = expenseCategoriesData.map((ec: any, idx: number) => ({
        name: ec.name,
        _id: ec._id ?? `ec-${idx}`,
        ecmev: ec.ecmev,
        color: ecColors[idx % ecColors.length] || '#ccc'
      }));

      setExpenseCategories(formattedExpenseCategories);
    } catch (error) {
      console.error('Error fetching expense categories:', error);
    }
  };

  const fetchBudgetData = async () => {
    try {
      const { data } = await budgetService.getBudget(budgetId || '');
      setBudgetData(data);
    } catch (error) {
      console.error('Error fetching budget data:', error);
    }
  };

  useEffect(() => {
    fetchExpenseCategories();
    fetchBudgetData();
  }, [budgetId]);

  // Only initialize editedValues once when expenseCategories are loaded
  useEffect(() => {
    if (expenseCategories.length > 0 && !isInitialized) {
      const initialValues: { [id: string]: number } = {};
      expenseCategories.forEach((ec: any) => {
        initialValues[ec._id] = ec.ecmev;
      });
      setEditedValues(initialValues);
      setIsInitialized(true);
    }
  }, [expenseCategories, isInitialized]);

  const handleSave = async () => {
    // Map editedValues to the desired array format
    const payload = Object.entries(editedValues).map(([id, value]) => ({
      id,
      ecmev: value
    }));

    try {
      // Note: You may need to add this service method to handle bulk adjustment
      await expenseRevenueService.adjustExpenseCategoriesAllocation(budgetId || '', { ecs: payload });
      toast.success('Allocation updated successfully');

      // Refresh the expense categories data to get updated values
      await fetchExpenseCategories();
    } catch (error) {
      console.error('Error adjusting allocation:', error);
      toast.error('Failed to update allocation');
    }
  };

  useEffect(() => {
    const total = Object.values(editedValues).reduce((acc, val) => acc + (Number(val) || 0), 0);
    setTotalAmount(total);
  }, [editedValues]);

  // Calculate chart data for ECMEV from total budget
  const ecmevTotal = totalBudget.ecmev;
  const ecmevUsed = totalBudget.ecmev_used;
  const ecmevRemaining = Math.max(0, ecmevTotal - ecmevUsed);

  const ecmevChartData = [
    {
      key: 'remaining',
      value: ecmevTotal === 0 ? 100 : Math.round((ecmevRemaining / ecmevTotal) * 100),
      fill: '#32ADE6'
    },
    {
      key: 'spent',
      value: ecmevTotal === 0 ? 0 : Math.round((ecmevUsed / ecmevTotal) * 100),
      fill: '#FF2D55'
    }
  ];

  const ecmevChartConfig = {
    remaining: { label: 'Remaining Budget', color: '#32ADE6' },
    spent: { label: 'Budget Spent', color: '#FF2D55' }
  };

  return (
    <>
      <MobileAppBar title="Edit Allocation" />

      <div className="flex flex-col p-4 lg:p-0 lg:flex-row gap-4 lg:gap-6 flex-1">
        <div className="h-fit flex items-center justify-between gap-4 md:border bg-white rounded-[16px] p-4 py-8">
          <div className="flex flex-col gap-2">
            <Tabs defaultValue="percentage" value={graphViewMode} onValueChange={setGraphViewMode}>
              <TabsList className="bg-lightPurple-200 h-auto py-[0.2rem]">
                <TabsTrigger value="percentage" className="group md:py-[0.3rem]">
                  <img
                    src={percentageIcon}
                    alt="percentage-icon"
                    width={13}
                    height={17}
                    className="group-data-[state=active]:brightness-[100]"
                  />
                </TabsTrigger>
                <TabsTrigger value="euro" className="group md:py-[0.3rem]">
                  <img
                    src={euroIcon}
                    alt="euro-icon"
                    width={12}
                    height={12}
                    className="w-[12px] h-[12px] group-data-[state=active]:brightness-[100]"
                  />
                </TabsTrigger>
              </TabsList>
            </Tabs>
            <p className="text-sm text-secondary">Remaining BMEV</p>
            <p className="text-[28px] font-bold leading-[24px] mb-2 md:mb-0">
              {graphViewMode === 'percentage' ? `${ecmevChartData[0].value}%` : `€${ecmevRemaining.toLocaleString()}`}
            </p>
            <div>
              <div className="flex items-center gap-2">
                <span className="w-[9px] h-[9px] rounded-full bg-[#32ADE6]"></span>
                <p className="text-sm text-secondary">Remaining Budget</p>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-[9px] h-[9px] rounded-full bg-[#FF2D55]"></span>
                <p className="text-sm text-secondary">Budget Spent</p>
              </div>
            </div>
          </div>
          <div>
            <PieGraph
              title="Remaining BMEV"
              titleValue={
                graphViewMode === 'percentage' ? `${ecmevChartData[0].value}%` : `€${ecmevRemaining.toLocaleString()}`
              }
              chartConfig={ecmevChartConfig}
              chartData={ecmevChartData}
              className=""
              outerRadius={64}
              minHeight={140}
              hideLabel={true}
            />
          </div>
        </div>

        <div className="flex-1 flex flex-col gap-4">
          {/* Allocation section title (MOBILE) */}
          <h4 className="text-[20px] font-bold mb-0 py-2 md:hidden">Allocation</h4>

          <div className="md:border bg-white rounded-[16px] p-4 flex-1 flex flex-col">
            <h4 className="text-[20px] font-bold mb-4 hidden md:block">Allocation</h4>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <p className="text-sm font-medium">ECMEV Allocation</p>
                <span className="text-primary text-xs font-bold">?</span>
              </div>
              <div className="text-sm text-secondary">
                <span className={`font-medium ${totalAmount !== maxAllowedTotal ? 'text-red-500' : 'text-green-600'}`}>
                  €{totalAmount.toLocaleString()}
                </span>
                <span className="mx-1">/</span>
                <span className="font-medium">€{maxAllowedTotal.toLocaleString()}</span>
              </div>
            </div>
            <div className="flex-1 mt-4">
              <div className={`${totalAmount !== maxAllowedTotal ? 'border border-[#E8507E] rounded-xl' : ''}`}>
                <AllocationBar
                  items={expenseCategories.map((ec) => ({
                    value: Number(editedValues[ec._id]) || 0,
                    label: ec.name,
                    color: ec.color
                  }))}
                  maxAllowedTotal={maxAllowedTotal}
                  height={22}
                  borderRadius={12}
                />
              </div>
              <div className="flex flex-col gap-4 mt-4">
                {expenseCategories.map((ec) => (
                  <div className="flex items-center justify-between" key={ec._id}>
                    <div className="flex-1 flex items-center gap-3">
                      <span className="w-[9px] h-[9px] rounded-full" style={{ backgroundColor: ec.color }}></span>
                      <p className="text-sm font-medium">{ec.name}</p>
                    </div>
                    <InputField
                      name="ecmev"
                      label=""
                      placeholder="10000"
                      type="text"
                      icon={euroIcon}
                      iconAlt="Euro"
                      className="w-[136px]"
                      size="sm"
                      value={
                        editedValues[ec._id] !== undefined && editedValues[ec._id] !== null
                          ? String(editedValues[ec._id])
                          : ''
                      }
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const value = e.target.value.replace(/[^\d.]/g, '');
                        const numericValue = value === '' ? 0 : Number(value);

                        // Calculate what the new total would be with this new value
                        const newTotal = Object.entries(editedValues).reduce((acc, [id, val]) => {
                          if (id === ec._id) {
                            return acc + numericValue;
                          }
                          return acc + (Number(val) || 0);
                        }, 0);

                        // If new total is within limit, allow the change
                        if (newTotal <= maxAllowedTotal) {
                          setEditedValues((prev) => ({ ...prev, [ec._id]: value === '' ? '' : numericValue }));
                        } else {
                          // Show warning toast and keep the previous value (don't update)
                          toast.error(`Total allocation cannot exceed €${maxAllowedTotal.toLocaleString()}`);
                        }
                      }}
                    />
                  </div>
                ))}
              </div>

              {/* Error message when total doesn't equal maximum */}
              {totalAmount !== maxAllowedTotal && (
                <p className="text-[#E8507E] text-sm font-medium mt-4">
                  The Allocation must have a value equal to the ECMEV (€{maxAllowedTotal.toLocaleString()})
                </p>
              )}
            </div>

            <div className="flex justify-end mt-4 gap-4">
              <button onClick={() => navigate(-1)} className="hidden md:block text-[#E8507E] text-sm font-semibold">
                Back
              </button>
              <Button
                className="w-full md:w-auto text-base md:text-sm uppercase md:capitalize bg-primary text-white rounded-full px-6"
                onClick={handleSave}
                disabled={totalAmount !== maxAllowedTotal}
              >
                Save
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ECAdjustPage;
